import { Dispatch, SetStateAction } from 'react';
import {
  CollegeItem,
  DropdownOption,
  EMPTY_FORM,
  ExposureForm,
  StudentRow,
} from './types';

interface MapperCtx {
  collegeMaster: CollegeItem[];
  districtList: DropdownOption[];
  setCollegeOptions: Dispatch<SetStateAction<DropdownOption[]>>;
  setDistrictList: Dispatch<SetStateAction<DropdownOption[]>>;
}

// Builds the edit-dialog form from a saved API row. API values can be numbers
// (ExpotStatus: 1, ClgId: 4) and the district arrives as a name, so we
// normalise and ensure the college/district dropdowns have matching options.
export function buildEditForm(row: StudentRow, ctx: MapperCtx): ExposureForm {
  const { collegeMaster, districtList, setCollegeOptions } = ctx;

  const expotStatus =
    row.ExpotStatus == '' || row.ExpotStatus == null
      ? ''
      : String(row.ExpotStatus);

  // Resolve the selected college from the master.
  const rawClgId =
    row.ClgId == '' || row.ClgId == null ? '' : Number(row.ClgId);
  const collegeMatch =
    (row.instititute_name
      ? collegeMaster.find(
          (c) =>
            (c.college_name ?? '').trim().toLowerCase() ==
            String(row.instititute_name).trim().toLowerCase()
        )
      : undefined) ?? collegeMaster.find((c) => c.college_id == rawClgId);
  const clgId: number | '' = collegeMatch
    ? collegeMatch.college_id
    : rawClgId !== '' && !Number.isNaN(rawClgId)
      ? rawClgId
      : '';

  // District value must be the numeric district_id (so the college filter
  // matches). Priority:
  //   1. "Others" college -> the record's saved district id (other_collage_dist)
  //   2. matched college's district_id (normal college)
  //   3. the record's saved district id
  //   4. the record's district as a numeric string / resolve the NAME
  const toNum = (v: any) =>
    v == null || v == '' || Number.isNaN(Number(v)) ? '' : Number(v);

  let distValue: string | number | '' = '';
  const savedDistId =
    toNum((row as any).district_id) ||
    toNum((row as any).OtherClgDist) ||
    toNum((row as any).other_collage_dist);

  const isOthers =
    String(row.instititute_name ?? '')
      .trim()
      .toLowerCase() === 'others';

  if (isOthers && savedDistId !== '') {
    distValue = savedDistId;
  } else if (collegeMatch && collegeMatch.district_id != null) {
    distValue = Number(collegeMatch.district_id);
  } else if (savedDistId !== '') {
    distValue = savedDistId;
  } else if (row.district != null && row.district !== '') {
    const d = String(row.district).trim();
    // district may be a numeric id or a name.
    const asNum = toNum(d);
    if (asNum !== '') {
      distValue = asNum;
    } else {
      const byName = districtList.find(
        (opt) => String(opt.label).trim().toLowerCase() == d.toLowerCase()
      );
      if (byName) distValue = byName.value as number;
    }
  }

  // Ensure the college option exists for the saved value (even "Others").
  if (clgId !== '' && row.instititute_name) {
    setCollegeOptions((prev) =>
      prev.some((o) => o.value == clgId)
        ? prev
        : [...prev, { value: clgId, label: String(row.instititute_name) }]
    );
  }

  const next: ExposureForm = {
    ...EMPTY_FORM,
    exposureVisit: expotStatus,
    collegeName: clgId,
    dist: distValue,
    visitDate: row.date_visit ?? '',
    OtherClgId: row.other_collage ?? '',
    teacher1: (row as any).staff_one ? Number((row as any).staff_one) : '',
    teacher2: (row as any).staff_two ? Number((row as any).staff_two) : '',
  };

  if (expotStatus == '2') {
    const noReason =
      row.No_reason == '' || row.No_reason == null ? '' : Number(row.No_reason);
    next.reason = noReason !== '' && !Number.isNaN(noReason) ? noReason : '';
    if (String(row.No_reason) == '12')
      next.OtherReason = row.Other_Reason ?? '';
  }

  return next;
}

// Resolve OtherClgDist to a numeric district id (API rejects a name string).
function resolveDistrictId(
  dist: ExposureForm['dist'],
  districtList: DropdownOption[]
): number | '' {
  if (dist == '' || dist == null) return '';
  if (typeof dist == 'number') return dist;
  const asNum = Number(dist);
  if (!Number.isNaN(asNum)) return asNum;
  const match = districtList.find(
    (opt) =>
      String(opt.label).trim().toLowerCase() ===
      String(dist).trim().toLowerCase()
  );
  return match ? Number(match.value) : '';
}

interface SaveCtx {
  indexId: string;
  schoolId: number;
  studId: string;
  districtList: DropdownOption[];
}

// Build the StudExpotureDetSave payload from the edit form.
// Only the fields relevant to the chosen status are sent; the other group is
// nulled so stale "Yes" data never persists when "No" is submitted (and vice
// versa).
export function buildSavePayload(form: ExposureForm, ctx: SaveCtx) {
  const isYes = form.exposureVisit == '1';
  const isNo = form.exposureVisit == '2';

  return {
    records: {
      IndxID: ctx.indexId || '',
      SchlId: ctx.schoolId,
      StudId: ctx.studId,
      ExpotStatus: form.exposureVisit,

      // "Yes" (visited) fields — nulled when the answer is "No".
      ClgId: isYes ? form.collegeName : null,
      OtherClgId: isYes ? form.OtherClgId : null,
      OtherClgDist: isYes
        ? resolveDistrictId(form.dist, ctx.districtList) || null
        : null,
      ExpotDate: isYes ? form.visitDate : null,
      Teacher1Id: isYes ? form.teacher1 || null : null,
      Teacher2Id: isYes ? form.teacher2 || null : null,

      // "No" (not visited) fields — nulled when the answer is "Yes".
      Noreason: isNo ? form.reason : null,
      OtherReason: isNo ? form.OtherReason : null,

      Isactive: '1',
    },
  };
}
